import React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from 'react-icons-kit';
import { ic_filter_list } from 'react-icons-kit/md/ic_filter_list';
import { buttonClasses } from 'styles/tailwindClassnames';
import { SECTION_LABEL } from './styles';
import { DragPathRef, StartPick } from './dragAndDrop';
import { JqBuilderState } from './useJqBuilderState';
import ConditionRow from './ConditionRow';
import DropSlot from './DropSlot';
import AdditionalRow from './AdditionalRow';
import LockedFieldSlot from './LockedFieldSlot';
import Alert from 'shared/components/ui/Alert';

// The Builder view: conditions, the object type's core fields, and additional fields.
// Complex fields are individually locked via LockedFieldSlot; simple fields remain editable.
const BuilderFields = ({
  builder,
  sampleInput,
  readOnly,
  dragPathRef,
  onStartPick,
  sourceArmed,
}: {
  builder: JqBuilderState;
  sampleInput: any;
  readOnly: boolean;
  dragPathRef: DragPathRef;
  onStartPick: StartPick;
  sourceArmed: boolean;
}) => {
  const { t } = useTranslation();
  const {
    spec,
    isComplexJq,
    complexFields,
    coreFieldMaps,
    transforms,
    conditions,
    additionalFields,
  } = builder;
  const complexAdditional = Object.entries(complexFields).filter(([key]) =>
    key.startsWith('addl:'),
  );
  return (
    <div className="flex-1 overflow-y-auto px-3.5 py-3">
      {/* Warning banner for complex jq in builder mode */}
      {isComplexJq && (
        <Alert status="warning" className="mb-3">
          {t(
            'Some fields use complex expressions that can only be edited in Code mode.',
          )}
        </Alert>
      )}

      {/* Conditions Section */}
      <div className="mb-4 border-b border-ui-divider-lines pb-4">
        <div className="mb-2 flex items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-2">
            <span
              aria-hidden="true"
              className="flex items-center text-greyscale-5"
            >
              <Icon icon={ic_filter_list} size={18} />
            </span>
            <span className={SECTION_LABEL}>{t('Conditions')}</span>
            {conditions.length === 0 && (
              <span className="truncate text-body-sm leading-5 text-greyscale-5">
                {t('None. All records pass through.')}
              </span>
            )}
          </div>
          {!readOnly && (
            <button
              onClick={builder.addCondition}
              className={`${buttonClasses.outlineXs} shrink-0`}
              type="button"
            >
              {t('Add')}
            </button>
          )}
        </div>
        {conditions.map((condition, index) => (
          <ConditionRow
            key={index}
            condition={condition}
            index={index}
            readOnly={readOnly}
            dragPathRef={dragPathRef}
            onUpdate={builder.updateCondition}
            onRemove={builder.removeCondition}
            onStartPick={onStartPick}
            sourceArmed={sourceArmed}
          />
        ))}
      </div>

      {/* Core Fields Section */}
      <div className="mb-4">
        <span className={`${SECTION_LABEL} mb-2 block`}>
          {t('Core Fields')}
        </span>
        {spec ? (
          spec.fields
            .filter((f) => f.required || f.showInCore)
            .map((f) => {
              if (complexFields[f.key]) {
                return (
                  <LockedFieldSlot
                    key={f.key}
                    label={f.labelKey}
                    expression={complexFields[f.key]}
                    required={f.required}
                    readOnly={readOnly}
                  />
                );
              }
              return (
                <DropSlot
                  key={f.key}
                  label={f.labelKey}
                  required={f.required}
                  mapping={coreFieldMaps[f.key] ?? null}
                  sampleInput={sampleInput}
                  readOnly={readOnly}
                  dragPathRef={dragPathRef}
                  onDrop={(p) => builder.updateCoreFieldMap(f.key, p)}
                  onClear={() => builder.updateCoreFieldMap(f.key, null)}
                  onStartPick={onStartPick}
                  sourceArmed={sourceArmed}
                  transforms={transforms[f.key]}
                  onChangeTransforms={(next) =>
                    builder.setTransformsFor(f.key, next)
                  }
                  transformsLocked={isComplexJq}
                />
              );
            })
        ) : (
          <div className="text-body-sm text-ui-text-primary-text">
            {t('Select an object type to see required fields')}
          </div>
        )}
      </div>

      {/* Additional Fields Section */}
      <div className="border-t border-ui-divider-lines pt-4">
        <div className="mb-2 flex items-center justify-between">
          <span className={SECTION_LABEL}>{t('Additional Fields')}</span>
          {!readOnly && (
            <button
              onClick={builder.addAdditionalField}
              className={buttonClasses.outlineXs}
              type="button"
            >
              {t('Add Field')}
            </button>
          )}
        </div>

        {additionalFields.map((field, index) => (
          <AdditionalRow
            key={`custom-${index}`}
            field={field}
            index={index}
            sampleInput={sampleInput}
            readOnly={readOnly}
            dragPathRef={dragPathRef}
            onUpdate={builder.updateAdditionalField}
            onRemove={builder.removeAdditionalField}
            onStartPick={onStartPick}
            sourceArmed={sourceArmed}
            transformsLocked={isComplexJq}
          />
        ))}

        {/* Complex additional fields (locked) */}
        {complexAdditional.map(([key, expr]) => (
          <LockedFieldSlot
            key={key}
            label={key.slice(5)}
            expression={expr}
            readOnly={readOnly}
          />
        ))}

        {builder.activeOptionalFields.length === 0 &&
          additionalFields.length === 0 &&
          complexAdditional.length === 0 && (
            <div className="text-body-sm text-ui-text-primary-text">
              {t('No additional fields')}
            </div>
          )}
      </div>
    </div>
  );
};

export default BuilderFields;
